<template>
    <v-dialog v-model="dialog" max-width="560" scrollable persistent :aria-labelledby="`${formId}-title`">
        <template v-slot:activator="{ on, attrs }">
            <v-btn
                v-if="tableView"
                class="branch-trigger"
                :class="{ 'branch-trigger--icon': iconOnly }"
                color="#f0784c"
                elevation="0"
                outlined
                small
                :icon="iconOnly"
                :aria-label="iconOnly ? 'Add account' : undefined"
                :title="iconOnly ? 'Add account' : undefined"
                v-bind="attrs"
                v-on="on"
            >
                <v-icon v-if="iconOnly">{{ mdiPlus }}</v-icon>
                <template v-else>Add Account</template>
            </v-btn>
            <v-btn
                v-if="!tableView"
                class="branch-trigger branch-trigger--tree"
                color="#db5120"
                v-bind="attrs"
                elevation="0"
                v-on="on"
            >
                Add User
            </v-btn>
        </template>
        <v-card
            class="branch-dialog"
            tag="form"
            dark
            elevation="0"
            novalidate
            :aria-busy="saving ? 'true' : 'false'"
            @submit.prevent="inviteUser"
        >
            <v-card-title class="branch-dialog__header">
                <p class="eyebrow">Organization membership</p>
                <h2 :id="`${formId}-title`">Invite a user</h2>
                <p>Choose who to invite and the access they need.</p>
            </v-card-title>
            <v-card-text class="branch-dialog__body">
                <section class="organization-context" aria-label="Target organization">
                    <span>Inviting to</span>
                    <h3>{{ organizationName }}</h3>
                    <p>Access applies to this organization.</p>
                </section>

                <div class="email-field">
                    <label :for="`${formId}-email`">Email address</label>
                    <p :id="`${formId}-email-help`" class="section-help">
                        Invite this person to join the organization with the role selected below.
                    </p>
                    <input
                        :id="`${formId}-email`"
                        v-model="accEmail"
                        name="email"
                        type="email"
                        autocomplete="email"
                        inputmode="email"
                        required
                        :disabled="saving"
                        :aria-invalid="emailError ? 'true' : 'false'"
                        :aria-describedby="`${formId}-email-help${emailError ? ` ${formId}-email-error` : ''}`"
                        @blur="emailTouched = true"
                    />
                    <p v-if="emailError" :id="`${formId}-email-error`" class="field-error" role="alert">
                        {{ emailError }}
                    </p>
                </div>

                <fieldset class="role-picker" :disabled="saving">
                    <legend>Organization role</legend>
                    <p :id="`${formId}-role-help`" class="section-help">Select one role for this invitation.</p>
                    <div class="role-options">
                        <label class="role-choice" :class="{ 'role-choice--selected': !adminSwitch }">
                            <input
                                v-model="adminSwitch"
                                type="radio"
                                :name="`${formId}-role`"
                                :value="false"
                                :aria-describedby="`${formId}-role-help ${formId}-member-help`"
                            />
                            <span>
                                <strong>Member</strong>
                                <span :id="`${formId}-member-help`"
                                    >Access organization services without managing members.</span
                                >
                            </span>
                        </label>
                        <label class="role-choice" :class="{ 'role-choice--selected': adminSwitch }">
                            <input
                                v-model="adminSwitch"
                                type="radio"
                                :name="`${formId}-role`"
                                :value="true"
                                :aria-describedby="`${formId}-role-help ${formId}-admin-help`"
                            />
                            <span>
                                <strong>Administrator</strong>
                                <span :id="`${formId}-admin-help`"
                                    >Manage organization members, roles, and settings.</span
                                >
                            </span>
                        </label>
                    </div>
                </fieldset>

                <section
                    class="invitation-summary"
                    aria-label="Review invitation"
                    aria-live="polite"
                    aria-atomic="true"
                >
                    <h3>Review invitation</h3>
                    <dl>
                        <div
                            ><dt>Recipient</dt><dd>{{ trimmedEmail || 'Enter an email address' }}</dd></div
                        >
                        <div
                            ><dt>Role</dt><dd>{{ selectedRole }}</dd></div
                        >
                        <div
                            ><dt>Organization</dt><dd>{{ organizationName }}</dd></div
                        >
                    </dl>
                </section>
                <p v-if="!organizationId" class="form-error" role="alert">
                    No organization is selected. Select an organization before sending an invitation.
                </p>
                <p v-if="saveError" class="form-error" role="alert">{{ saveError }}</p>
            </v-card-text>
            <v-card-actions class="branch-dialog__actions">
                <v-btn class="branch-secondary cancel-invitation" type="button" text :disabled="saving" @click="cancel">
                    Cancel
                </v-btn>
                <v-btn
                    class="branch-primary send-invitation"
                    type="submit"
                    color="#db5120"
                    elevation="0"
                    :loading="saving"
                    :disabled="!canInvite"
                    >Send invitation</v-btn
                >
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
import gql from 'graphql-tag'
import { mdiPlus } from '@mdi/js'

export default {
    name: 'AddUser',
    props: {
        currentLevel: {
            type: Object,
            required: true
        },
        tableView: {
            type: Boolean,
            required: true
        },
        iconOnly: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            mdiPlus,
            dialog: false,
            accEmail: '',
            adminSwitch: false,
            emailTouched: false,
            saving: false,
            saveError: '',
            formVersion: 0
        }
    },
    computed: {
        formId() {
            return `invitation-${this._uid}`
        },
        organizationId() {
            return this.currentLevel.baseAccountId
        },
        organizationName() {
            return this.currentLevel.baseAccountName || this.currentLevel.organizationName || 'this organization'
        },
        trimmedEmail() {
            return this.accEmail.trim()
        },
        validEmail() {
            return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.trimmedEmail)
        },
        emailError() {
            if (!this.emailTouched || this.validEmail) return ''
            return this.trimmedEmail
                ? 'Enter a valid email address, such as name@example.com.'
                : 'Enter an email address.'
        },
        selectedRole() {
            return this.adminSwitch ? 'Administrator' : 'Member'
        },
        canInvite() {
            return this.dialog && this.validEmail && !!this.organizationId && !this.saving
        }
    },
    watch: {
        dialog(open) {
            if (open) this.resetForm()
            else this.formVersion += 1
        },
        organizationId() {
            this.resetForm()
        }
    },
    beforeDestroy() {
        this.formVersion += 1
    },
    methods: {
        resetForm() {
            this.formVersion += 1
            this.accEmail = ''
            this.adminSwitch = false
            this.emailTouched = false
            this.saveError = ''
        },
        cancel() {
            if (!this.saving) this.dialog = false
        },
        failureMessage(detail) {
            const reason = typeof detail === 'string' && detail.trim() ? ` ${detail}` : ''
            return `Could not send the invitation.${reason} Please try again.`
        },
        async inviteUser() {
            if (!this.dialog || this.saving) return
            this.emailTouched = true
            if (!this.canInvite) return
            this.saving = true
            this.saveError = ''
            const version = this.formVersion
            const email = this.trimmedEmail
            const orgId = this.organizationId
            const organizationName = this.organizationName
            const isCurrent = () => version === this.formVersion && orgId === this.organizationId && this.dialog
            try {
                const response = await this.$apollo.mutate({
                    mutation: gql`
                        mutation AddUserToOrganization($admin: Boolean!, $email: String!, $orgId: String!) {
                            addUserToOrganization(admin: $admin, email: $email, orgId: $orgId) {
                                complete
                                error
                                success
                                uid
                            }
                        }
                    `,
                    variables: {
                        admin: this.adminSwitch,
                        email,
                        orgId
                    }
                })
                if (!isCurrent()) return
                const result = response && response.data && response.data.addUserToOrganization
                if (result && result.success === true) {
                    this.dialog = false
                    this.$emit('added', `'${email}' has been invited to join ${organizationName}`)
                } else {
                    this.saveError = this.failureMessage(result && result.error)
                }
            } catch (error) {
                if (isCurrent()) this.saveError = this.failureMessage(error && error.message)
            } finally {
                this.saving = false
            }
        }
    }
}
</script>

<style scoped>
.branch-trigger,
.branch-dialog {
    font-family: 'Inter Tight', 'Inter', sans-serif;
    font-size: 12px;
}
.branch-trigger {
    border-radius: 8px;
    letter-spacing: 0;
    text-transform: none;
}
.branch-trigger--icon {
    width: 36px;
    min-width: 36px !important;
    height: 36px;
    padding: 0 !important;
    border-radius: 9px;
}
.branch-trigger--icon .v-icon {
    color: #ffb396;
}
.branch-trigger--tree {
    display: block;
    margin: auto;
    min-width: 150px;
    color: #fff;
}
.branch-dialog.v-card {
    display: flex;
    flex-direction: column;
    max-height: calc(100vh - 48px);
    border: 1px solid #454953;
    border-radius: 12px;
    background: #282b33;
    color: #f5f5f7;
}
.branch-dialog__header {
    flex: 0 0 auto;
    display: block;
    padding: 20px 24px;
    border-bottom: 1px solid #454953;
}
.branch-dialog h2 {
    margin: 0;
    font-size: 22px;
    font-weight: 600;
    line-height: 1.25;
    letter-spacing: -0.02em;
    overflow-wrap: anywhere;
}
.branch-dialog .branch-dialog__body {
    min-height: 0;
    padding: 20px 24px;
    overflow-y: auto;
    color: #d4d6dd;
    font-size: 12px;
    line-height: 1.6;
}
.branch-dialog__header p {
    margin: 8px 0 0;
    color: #bfc3ce;
    font-size: 12px;
    line-height: 1.5;
}
.branch-dialog__header .eyebrow {
    margin: 0 0 8px;
    color: #f0784c;
    font-weight: 600;
}
.organization-context {
    margin-bottom: 20px;
    padding: 12px 14px;
    border: 1px solid #454953;
    border-radius: 12px;
    background: #22252d;
    overflow-wrap: anywhere;
}
.organization-context > span {
    color: #f0784c;
    font-weight: 600;
}
.branch-dialog h3 {
    margin: 0;
    color: #f5f5f7;
    font-size: 14px;
    line-height: 1.5;
}
.branch-dialog .organization-context p {
    margin: 4px 0 0;
    color: #bfc3ce;
}
.email-field {
    margin-bottom: 20px;
}
.email-field > label,
.role-picker legend {
    display: block;
    margin-bottom: 4px;
    color: #f5f5f7;
    font-size: 14px;
    font-weight: 600;
}
.branch-dialog .section-help {
    margin: 0 0 12px;
    color: #bfc3ce;
}
.email-field input {
    width: 100%;
    min-height: 40px;
    padding: 10px 12px;
    border: 1px solid #686d79;
    border-radius: 8px;
    background: #22252d;
    color: #f5f5f7;
    font: inherit;
}
.email-field input[aria-invalid='true'] {
    border-color: #ffb4ac;
}
.email-field input:disabled {
    opacity: 0.6;
}
.branch-dialog .field-error {
    margin: 6px 0 0;
    color: #ffb4ac;
}
.role-picker {
    min-width: 0;
    margin: 0;
    padding: 0;
    border: 0;
}
.role-options {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 10px;
}
.role-choice {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    padding: 14px;
    border: 1px solid #686d79;
    border-radius: 12px;
    background: #22252d;
    cursor: pointer;
}
.role-choice--selected {
    border-color: #f0784c;
    background: #353036;
}
.role-choice input {
    flex: 0 0 16px;
    width: 16px;
    height: 16px;
    margin-top: 3px;
    accent-color: #f0784c;
}
.role-choice strong {
    display: block;
    margin-bottom: 4px;
    color: #f5f5f7;
    font-size: 14px;
}
.role-choice span span {
    display: block;
    color: #bfc3ce;
}
.role-picker:disabled .role-choice {
    opacity: 0.6;
    cursor: not-allowed;
}
.invitation-summary {
    margin-top: 16px;
    padding: 12px 14px;
    border-left: 3px solid #f0784c;
    border-radius: 0 8px 8px 0;
    background: #22252d;
    overflow-wrap: anywhere;
}
.invitation-summary dl {
    margin-top: 8px;
}
.invitation-summary dl > div {
    display: grid;
    grid-template-columns: 90px minmax(0, 1fr);
    gap: 12px;
    margin-top: 4px;
}
.invitation-summary dt {
    color: #bfc3ce;
}
.invitation-summary dd {
    margin: 0;
}
.branch-dialog .form-error {
    margin: 12px 0 0;
    padding: 12px;
    border: 1px solid #a45c58;
    border-radius: 8px;
    color: #ffb4ac;
    overflow-wrap: anywhere;
}
.branch-dialog__actions {
    flex: 0 0 auto;
    justify-content: flex-end;
    flex-wrap: wrap;
    gap: 8px;
    padding: 16px 24px;
    border-top: 1px solid #454953;
    background: #22252d;
}
.branch-dialog .v-btn {
    max-width: 100%;
    height: auto;
    min-height: 36px;
    margin: 0;
    padding: 8px 16px;
    border-radius: 8px;
    font-size: 12px;
    letter-spacing: 0;
    text-transform: none;
}
.branch-dialog .branch-secondary {
    border-color: #686d79;
    color: #e0e2e8;
}
.branch-dialog .branch-primary {
    color: #fff;
}
.branch-dialog ::v-deep .v-btn__content {
    white-space: normal;
    overflow-wrap: anywhere;
}
.branch-trigger:focus-visible,
.email-field input:focus-visible,
.role-choice:focus-within,
.branch-dialog ::v-deep .v-btn:focus-visible {
    outline: 2px solid #f0784c;
    outline-offset: 3px;
}
@media (max-width: 600px) {
    .branch-dialog__header,
    .branch-dialog .branch-dialog__body,
    .branch-dialog__actions {
        padding: 16px;
    }
    .role-options {
        grid-template-columns: minmax(0, 1fr);
    }
    .invitation-summary dl > div {
        grid-template-columns: minmax(0, 1fr);
        gap: 0;
    }
}
</style>
